import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Lock, Save, Check, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const MIN_LENGTH = 8;

export default function AccountSettingsModal({ open, onOpenChange, user }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [saving, setSaving] = useState(false);

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            setCurrent("");
            setNext("");
            setConfirm("");
            setShowCurrent(false);
            setShowNext(false);
        }
    }, [open]);

    const lengthOk = next.length >= MIN_LENGTH;
    const matches = next.length > 0 && next === confirm;
    const differs = current.length > 0 && next.length > 0 && current !== next;
    const allValid = current.length > 0 && lengthOk && matches && differs;

    const submit = async (e) => {
        e.preventDefault();
        if (!allValid) return;
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Passwort aktualisiert");
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Aktualisieren");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="account-settings-modal"
                className="max-w-md p-0 gap-0 overflow-hidden"
            >
                {/* Header */}
                <div className="bg-aimploy-navy text-white p-6 lg:p-8">
                    <DialogHeader>
                        <div className="h-12 w-12 rounded-xl bg-aimploy-violet flex items-center justify-center mb-4">
                            <Lock className="w-5 h-5" />
                        </div>
                        <DialogTitle className="font-display text-2xl font-bold text-white">
                            Konto-Einstellungen
                        </DialogTitle>
                        <DialogDescription className="text-white/70 mt-1">
                            Angemeldet als <strong className="text-white">{user?.email}</strong>
                        </DialogDescription>
                    </DialogHeader>
                </div>

                {/* Body */}
                <form onSubmit={submit} className="p-6 lg:p-8 space-y-5">
                    <h3 className="font-display text-lg font-bold">Passwort ändern</h3>

                    <PasswordField
                        label="Aktuelles Passwort"
                        value={current}
                        onChange={setCurrent}
                        show={showCurrent}
                        toggleShow={() => setShowCurrent(!showCurrent)}
                        testId="current-password"
                        autoComplete="current-password"
                    />

                    <PasswordField
                        label="Neues Passwort"
                        value={next}
                        onChange={setNext}
                        show={showNext}
                        toggleShow={() => setShowNext(!showNext)}
                        testId="new-password"
                        autoComplete="new-password"
                    />

                    <PasswordField
                        label="Neues Passwort bestätigen"
                        value={confirm}
                        onChange={setConfirm}
                        show={showNext}
                        toggleShow={() => setShowNext(!showNext)}
                        testId="confirm-password"
                        autoComplete="new-password"
                    />

                    {/* Validation */}
                    <ul className="space-y-1.5 text-xs">
                        <Rule ok={lengthOk}>Mindestens {MIN_LENGTH} Zeichen</Rule>
                        <Rule ok={differs}>Unterscheidet sich vom aktuellen Passwort</Rule>
                        <Rule ok={matches}>Beide neue Passwörter stimmen überein</Rule>
                    </ul>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="flex-1 px-5 py-3 rounded-xl border border-aimploy-navy/15 text-sm font-semibold hover:bg-aimploy-bg"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={!allValid || saving}
                            data-testid="account-save"
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-aimploy-navy text-white px-5 py-3 rounded-xl text-sm font-semibold hover:bg-aimploy-navy-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? "Speichern…" : "Passwort ändern"}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function PasswordField({ label, value, onChange, show, toggleShow, testId, autoComplete }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2 block">
                {label}
            </label>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testId}
                    autoComplete={autoComplete}
                    className="w-full border border-aimploy-navy/15 rounded-xl pl-4 pr-12 py-3 text-sm"
                />
                <button
                    type="button"
                    onClick={toggleShow}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-aimploy-muted hover:text-aimploy-navy"
                    aria-label={show ? "Passwort verbergen" : "Passwort anzeigen"}
                    tabIndex={-1}
                >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
}

function Rule({ ok, children }) {
    return (
        <li className={`flex items-center gap-2 ${ok ? "text-emerald-600" : "text-aimploy-muted"}`}>
            <span
                className={`h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                    ok ? "bg-emerald-100" : "bg-aimploy-bg border border-aimploy-navy/10"
                }`}
            >
                {ok && <Check className="w-2.5 h-2.5" />}
            </span>
            {children}
        </li>
    );
}
